import { Select } from '../common/AdminUI';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Plus, Trash2, FileText, ChevronDown, GripVertical, Search, UserPlus, User, Calendar, ChevronLeft, ChevronRight, AlertTriangle, CheckCircle2, Package, Barcode, Building2, Truck, MapPin, Scale, Boxes, Landmark, ClipboardCheck, Eye, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import InvoicePreview from './InvoicePreview';
import { apiCall, API_ENDPOINTS } from '../../config/api';
import { useServices, getServices } from '../../config/servicesSource';

// A saved invoice line stores the service's label and HSN, not its key, so
// match it back to the list by either.
const resolveService = (services, description, hsn) => {
  const desc = String(description || '').toLowerCase();
  return services.find(s => s.label.toLowerCase() === desc)
    || (hsn ? services.find(s => s.hsn === String(hsn)) : null)
    || null;
};

// Qty on an invoice is always a whole number — including air freight, where it
// comes off the chargeable weight (e.g. 567.000 kg bills as 567).
const toWholeQty = (value) => {
  const n = parseFloat(String(value ?? '').replace(/,/g, ''));
  if (!Number.isFinite(n) || n <= 0) return '';
  return String(Math.max(1, Math.round(n)));
};

// S/B and LEO dates arrive as an ISO string, a dd/mm/yyyy string, an array, or
// several joined with "&" — show each one the way the rest of the app prints
// dates.
const formatShipmentDate = (value) => {
  if (Array.isArray(value)) return formatShipmentDate(value.join(' & '));
  const raw = String(value ?? '').trim();
  if (!raw || raw === '-') return '';
  return raw
    .split('&')
    .map((part) => {
      const piece = part.trim();
      if (!piece) return '';
      if (!/^\d{4}-\d{2}-\d{2}/.test(piece)) return piece;
      const d = new Date(piece);
      return Number.isNaN(d.getTime())
        ? piece
        : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    })
    .filter(Boolean)
    .join(' & ');
};

// GET /invoices/max-invoice-number returns the highest number already issued
// for a prefix. Its payload may wrap the value (data/result) and may give the
// whole number ("HMR/2025-26/AE/017") or just the count — read either, and
// return null when the series is empty so the caller starts at 001.
const readMaxInvoice = (payload) => {
  const fromValue = (v) => {
    if (typeof v === 'number' && Number.isFinite(v) && v > 0) return { count: Math.trunc(v), number: null };
    if (typeof v === 'string') {
      const m = v.trim().match(/(\d+)\s*$/);
      if (!m) return null;
      const count = parseInt(m[1], 10);
      if (!count) return null;
      return { count, number: v.includes('/') ? v.trim() : null };
    }
    return null;
  };
  const VALUE_KEYS = [
    'maxInvoiceNumber', 'max_invoice_number', 'maxNumber', 'max_number',
    'invoiceNumber', 'invoice_number', 'lastInvoiceNumber', 'last_invoice_number',
    'max', 'maxCount', 'max_count', 'count', 'sequence', 'value',
  ];
  const walk = (node, depth) => {
    if (node == null || depth > 4) return null;
    const direct = fromValue(node);
    if (direct) return direct;
    if (typeof node !== 'object') return null;
    for (const key of VALUE_KEYS) {
      if (key in node) {
        const hit = walk(node[key], depth + 1);
        if (hit) return hit;
      }
    }
    for (const key of ['data', 'result', 'invoice']) {
      if (key in node) {
        const hit = walk(node[key], depth + 1);
        if (hit) return hit;
      }
    }
    return null;
  };
  return walk(payload, 0);
};

// `customerInvoice` raises an invoice straight against a customer: no shipment,
// so the shipment header, the charges pulled from its payment activity and the
// shipment block in the PDF all drop out. The number still comes from the same
// series.
const InvoiceGenerationPage = ({ shipment = {}, existingInvoice, allInvoices = [], customerInvoice = false, customerUsers = [], onClose }) => {
  // existingInvoice can arrive as an array (from invoiceMap, e.g. table "Edit Invoice" buttons)
  // or a single object (e.g. shipment detail modal) — normalize once so .id lookups work either way.
  const normalizedExistingInvoice = Array.isArray(existingInvoice) ? existingInvoice[0] : existingInvoice;
  const [showPreview, setShowPreview] = useState(false);
  const [descDropdown, setDescDropdown] = useState({}); // { index: { open, search } }
  const descDropdownRef = useRef({});
  const [lineItemErrors, setLineItemErrors] = useState({}); // { index: { description, rate, qty } }
  const [customerError, setCustomerError] = useState(false);
  const [customerHighlightIdx, setCustomerHighlightIdx] = useState(-1);
  const [descHighlightIdx, setDescHighlightIdx] = useState({}); // { index: number }

  // Customer section state
  const [customerList, setCustomerList] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', gstNo: '', address: '' });
  const [addingCustomer, setAddingCustomer] = useState(false);
  // Shipper — populated from GST-matched customer name
  const [shipperName, setShipperName] = useState('');
  const customerDropdownRef = useRef(null);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [calViewDate, setCalViewDate] = useState(() => new Date());
  const datePickerRef = useRef(null);
  const [removedAutoItems, setRemovedAutoItems] = useState([]);

  const [invoiceManualMode, setInvoiceManualMode] = useState(true);

  // invoice_data fetched from API (latest invoices history)
  const [invoiceDataHistory, setInvoiceDataHistory] = useState([]);
  // Highest number already issued across the whole HMR/<FY>/<AE|SE> series.
  const [seriesMax, setSeriesMax] = useState(null);   // { count, number } | null
  const [seriesChecked, setSeriesChecked] = useState(false);
  // Once the number has been typed by hand, the series answer must not
  // overwrite it when it lands.
  const invoiceNumberTouchedRef = useRef(false);
  const isSeaShipment = String(shipment?.type || shipment?.shipmentType || '').toLowerCase().includes('sea');
  const shipmentIdentifierLabel = isSeaShipment ? 'B/L' : 'AWB';
  const shipmentIdentifier = String(isSeaShipment ? (shipment?.blNumber || shipment?.bl_number || '') : (shipment?.awbNumber || shipment?.awb_number || '')).trim();
  const invoiceSeriesCode = isSeaShipment ? 'SE' : 'AE';

  // Get financial year based on SB date (April-March cycle)
  const getFinancialYear = () => {
    // Get SB date from shipment
    const sbDateRaw = shipment?.sbDate || shipment?.sb_date || 
      (Array.isArray(shipment?.sbDates) && shipment.sbDates[0]) || '';
    
    let dateToUse = new Date();
    if (sbDateRaw) {
      // Month name mapping for formats like "20-JAN-026" or "20-JAN-2026"
      const monthMap = {
        'JAN': 0, 'FEB': 1, 'MAR': 2, 'APR': 3, 'MAY': 4, 'JUN': 5,
        'JUL': 6, 'AUG': 7, 'SEP': 8, 'OCT': 9, 'NOV': 10, 'DEC': 11
      };

      // Try DD/MM/YYYY format first (e.g., "01/04/2026")
      const ddmmyyyyMatch = sbDateRaw.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})/);
      if (ddmmyyyyMatch) {
        const [, day, month, year] = ddmmyyyyMatch;
        dateToUse = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
      // Try DD-MMM-YYY or DD-MMM-YYYY format (e.g., "20-JAN-026 00:00:00")
      else {
        const dmmyMatch = sbDateRaw.match(/^(\d{1,2})-(\w{3})-(\d{2,4})/);
        if (dmmyMatch) {
          const [, day, monthStr, yearStr] = dmmyMatch;
          let yearNum = parseInt(yearStr);
          // Handle 3-digit year like "026" → 2026
          if (yearNum < 100) yearNum += 2000;
          const monthIdx = monthMap[monthStr.toUpperCase()];
          if (monthIdx !== undefined) {
            dateToUse = new Date(yearNum, monthIdx, parseInt(day));
          }
        }
        // Try YYYY-MM-DD format
        else {
          const isoMatch = sbDateRaw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
          if (isoMatch) {
            const [, year, month, day] = isoMatch;
            dateToUse = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          }
        }
      }
    }
    
    const month = dateToUse.getMonth(); // 0-11
    const year = dateToUse.getFullYear();
    // Financial year: April (month 3) onwards = current year, Jan-Mar = previous year
    const fy = month >= 3 ? year : year - 1;
    return `${fy}-${String(fy + 1).slice(-2)}`;
  };

  // Fallback: generate invoice number locally if API fails
  const generateInvoiceNumberLocal = () => {
    const fyShort = getFinancialYear();
    return `HMR/${fyShort}/${invoiceSeriesCode}/001`;
  };

  // The locked prefix — everything before the last "/" segment
  const getInvoicePrefix = (invoiceNum) => {
    const lastSlash = (invoiceNum || '').lastIndexOf('/');
    return lastSlash > 0 ? invoiceNum.slice(0, lastSlash + 1) : `HMR/${invoiceSeriesCode}/`;
  };
  const getInvoiceSuffix = (invoiceNum) => {
    const lastSlash = (invoiceNum || '').lastIndexOf('/');
    return lastSlash > 0 ? invoiceNum.slice(lastSlash + 1) : invoiceNum;
  };

  const normalizedShipmentUserId = (() => {
    const rawUserId = shipment?.userId ?? shipment?.userID ?? shipment?.user_id ?? null;
    const parsed = rawUserId !== null && rawUserId !== undefined && rawUserId !== '' ? parseInt(rawUserId, 10) : NaN;
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  })();

  const [formData, setFormData] = useState(() => {
    // ── Pre-fill from existing invoice when editing ──
    // API may return camelCase (Sequelize ORM) or snake_case (raw SQL)
    if (existingInvoice) {
      // existingInvoice can be an array (from invoiceMap) or single object
      const raw = Array.isArray(existingInvoice) ? existingInvoice[0] : existingInvoice;
      const inv = raw?.invoice || raw;
      const bankRaw = inv.payment?.bank_name || inv.payment?.bankName || '';
      const bankName = bankRaw.toUpperCase().includes('HDFC') ? 'HDFC' : 'ICICI';
      const rawItems = inv.items || [];
      // Parse items if stored as JSON string
      const parsedItems = typeof rawItems === 'string' ? JSON.parse(rawItems) : rawItems;
      const lineItems = parsedItems.map(item => {
        // Reverse-lookup label → value in the service list
        const matched = resolveService(getServices(), item.description, item.hsn_sac || item.hsnSac);
        return {
          description: matched ? matched.value : (item.description || ''),
          hsn: item.hsn_sac || item.hsnSac || '',
          rate: String(item.unit_price || item.unitPrice || ''),
          qty: toWholeQty(item.quantity) || '1',
          gst: String(item.gst_rate_percent || item.gstRatePercent || '18')
        };
      });
      // Only use invoiceData array — ignore legacy top-level invoiceNumber/invoiceDate fields
      const invDataArr = (() => { const d = inv.invoiceData || inv.invoice_data || []; return typeof d === 'string' ? JSON.parse(d) : (Array.isArray(d) ? d : []); })();
      const invoiceNumber = invDataArr[0]?.invoiceNumber || generateInvoiceNumberLocal();
      const invoiceDate = invDataArr[0]?.invoiceDate || new Date().toISOString().split('T')[0];
      const buyerRaw = typeof inv.buyer === 'string' ? JSON.parse(inv.buyer) : inv.buyer;
      return {
        supplyType: 'B2C',
        bankName,
        invoiceNumber,
        invoiceDate,
        placeOfSupply: 'Delhi',
        // Kept so a re-save keeps the invoice billed to the same client row.
        customerId: buyerRaw?.customer_id ?? buyerRaw?.customerId ?? raw?.customerId ?? raw?.customer_id ?? null,
        customerGSTIN: buyerRaw?.gstin || '',
        customerName: buyerRaw?.name || '',
        customerAddress: buyerRaw?.address || '',
        lineItems: lineItems.length > 0 ? lineItems : [{ description: '', hsn: '', rate: '', qty: '1', gst: '' }]
      };
    }
    // ── Fresh invoice — will be updated by API fetch ──
    return {
      supplyType: 'B2C',
      bankName: 'ICICI',
      invoiceNumber: generateInvoiceNumberLocal(),
      invoiceDate: new Date().toISOString().split('T')[0],
      placeOfSupply: 'Delhi',
      customerId: null,
      customerGSTIN: '',
      customerName: '',
      customerAddress: '',
      lineItems: [
        {
          description: '',
          hsn: '',
          rate: '',
          qty: '1',
          gst: ''
        }
      ]
    };
  });

  // Description options with HSN and GST mapping — maintained by admins under
  // HSN Codes, with the bundled list as the fallback.
  const descriptionOptions = useServices();

  // The form may have been built from the cached list before the API answered;
  // once it lands, map any line item that didn't resolve to a service then.
  useEffect(() => {
    setFormData(prev => {
      const lineItems = prev.lineItems.map(item => {
        if (!item.description || descriptionOptions.some(o => o.value === item.description)) return item;
        const matched = resolveService(descriptionOptions, item.description, item.hsn);
        return matched ? { ...item, description: matched.value, hsn: item.hsn || matched.hsn } : item;
      });
      return lineItems.some((it, i) => it !== prev.lineItems[i]) ? { ...prev, lineItems } : prev;
    });
  }, [descriptionOptions]);

  // ── Auto items from shipment paymentActivity + paymentsAmount ──
  const autoItems = React.useMemo(() => {
    if (customerInvoice) return [];
    // AccountantDashboard passes activity/amount as comma-joined strings
    // EmployeeDashboard passes paymentActivity as array — support both
    let activities = [];
    let amounts = [];

    if (Array.isArray(shipment?.paymentActivity) && shipment.paymentActivity.length) {
      activities = shipment.paymentActivity;
      amounts = Array.isArray(shipment?.paymentsAmount) ? shipment.paymentsAmount : [];
    } else if (shipment?.activity && shipment.activity !== '-') {
      // joinArray uses ' & ' as separator — split by that
      activities = String(shipment.activity).split(' & ').map(s => s.trim()).filter(Boolean);
      const rawAmt = shipment?.amount && shipment.amount !== '-' ? String(shipment.amount) : '';
      // Split by ' & ' first, then clean each part
      amounts = rawAmt ? rawAmt.split(' & ').map(s => s.trim()).filter(Boolean) : [];
    }
    // Shortcode aliases → services.json value
    const aliases = {
      'tspch': 'tsp', 'tsp ch': 'tsp',
      'doc': 'documentation', 'doc charges': 'documentation', 'docch': 'documentation',
      'mkch': 'marking', 'marking ch': 'marking',
      'aai': 'aai_terminal', 'aaich': 'aai_terminal', 'aai ter': 'aai_terminal',
      'acmes': 'acmes',
      'af': 'air_freight', 'airfreight': 'air_freight', 'air frt': 'air_freight',
      'dem': 'demurage', 'demch': 'demurage',
      'awbch': 'awb_pca', 'awb ch': 'awb_pca', 'pca': 'awb_pca',
      'ams': 'ams',
      'airline': 'airlines', 'airlinech': 'airlines',
      'awb': 'awb',
      'xray': 'xray', 'x-ray': 'xray',
      'cust': 'customs', 'custch': 'customs', 'cc': 'customs',
      'handling': 'handling', 'handlingch': 'handling', 'hch': 'handling',
      'transport': 'transportation', 'trans': 'transportation', 'trch': 'transportation',
      'terminal': 'terminal', 'terch': 'terminal',
      'misc': 'misc', 'miscch': 'misc',
      'storage': 'storage_warehouse', 'storagech': 'storage_warehouse',
      'warehouse': 'warehouse', 'whch': 'warehouse',
      'bl': 'bl', 'blch': 'bl',
      'ihc': 'ihc', 'ihcch': 'ihc',
      'concor': 'concor',
      'cmc': 'cmc',
      'vgm': 'vgm',
      'pkg': 'packaging', 'pack': 'packaging',
    };

    const isDcsc = String(shipment?.service || shipment?.shipmentType || 'gmr').toLowerCase().includes('dcsc');

    const result = [];
    activities.forEach((act, i) => {
      const rawAmt = parseFloat(String(amounts[i] || '0').replace(/,/g, '')) || 0;
      if (!act || rawAmt <= 0) return;
      const actLower = String(act).toLowerCase().trim();
      // 1. Exact label match
      let matched = descriptionOptions.find(s => s.label.toLowerCase() === actLower);
      // 2. Alias/shortcode match
      if (!matched && aliases[actLower]) {
        matched = descriptionOptions.find(s => s.value === aliases[actLower]);
      }
      // 3. Partial label match (activity is substring of label)
      if (!matched) {
        matched = descriptionOptions.find(s => s.label.toLowerCase().includes(actLower) || actLower.includes(s.label.toLowerCase().split(' ')[0]));
      }
      // GMR stores the charge without GST, so it goes on as the rate and 18%
      // is added on top. DCSC stores it with GST already in, so the taxable
      // value has to be taken back out of it.
      const baseRate = isDcsc ? parseFloat((rawAmt / 1.18).toFixed(2)) : rawAmt;
      result.push({
        description: matched ? matched.value : act,
        hsn: matched ? matched.hsn : '',
        rate: String(baseRate),
        qty: '1',
        gst: '18',
        _auto: true, // marker: not editable / not draggable
        _label: matched ? matched.label : String(act),
        _originalAmt: rawAmt, // the shipment's charge, as stored
      });
    });
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipment, descriptionOptions]);

  // Bank details mapping
  const bankDetails = {
    ICICI: {
      beneficiaryName: 'HMR Spedition Pvt. Ltd.',
      bankName: 'ICICI Bank',
      bankBranch: 'Connaught Place New Delhi',
      accountNumber: '000705055832',
      ifscCode: 'ICIC0000007',
      swiftCode: 'ICICINBBCTS'
    },
    HDFC: {
      beneficiaryName: 'HMR Spedition Pvt Ltd',
      bankName: 'HDFC Bank',
      bankBranch: 'Paharganj New Delhi',
      accountNumber: '50200096618918',
      ifscCode: 'HDFC0000457',
      swiftCode: 'HDFCINBBXXX'
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      const anyOpen = Object.values(descDropdown).some(d => d?.open);
      if (anyOpen) {
        setDescDropdown(prev => {
          const updated = {};
          Object.keys(prev).forEach(k => { updated[k] = { ...prev[k], open: false }; });
          return updated;
        });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [descDropdown]);

  // The series this invoice belongs to: financial year (from the S/B date)
  // plus AE for air, SE for sea.
  const seriesPrefix = `HMR/${getFinancialYear()}/${invoiceSeriesCode}`;
  const nextInSeries = (count) => `${seriesPrefix}/${String(count + 1).padStart(3, '0')}`;
  const seriesMaxNumber = seriesMax
    ? (seriesMax.number || `${seriesPrefix}/${String(seriesMax.count).padStart(3, '0')}`)
    : null;

  // Numbering runs across the whole series, not per shipment, so ask the API
  // what the last one was instead of counting this AWB's own invoices.
  useEffect(() => {
    let cancelled = false;
    setSeriesChecked(false);
    setSeriesMax(null);
    (async () => {
      try {
        const res = await apiCall(
          `${API_ENDPOINTS.INVOICE_MAX_NUMBER}?prefix=${encodeURIComponent(seriesPrefix)}`,
          { method: 'GET' }
        );
        const payload = await res.json().catch(() => null);
        if (cancelled) return;
        if (res.ok && payload?.success !== false) setSeriesMax(readMaxInvoice(payload));
      } catch {
        // Offline or the endpoint isn't deployed yet — fall back to counting
        // this shipment's own invoices, exactly as before.
      } finally {
        if (!cancelled) setSeriesChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [seriesPrefix]);

  useEffect(() => {
    if (existingInvoice || invoiceNumberTouchedRef.current || !seriesMax) return;
    const next = nextInSeries(seriesMax.count);
    setFormData(prev => (prev.invoiceNumber === next ? prev : { ...prev, invoiceNumber: next }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesMax, seriesPrefix, existingInvoice]);

  // ── Compute next invoice number + history from allInvoices (per AWB) ──
  useEffect(() => {
    const currentIdentifier = shipmentIdentifier;

    if (!currentIdentifier) {
      setInvoiceDataHistory([]);
      return;
    }

    // Filter invoices for THIS shipment identifier only
    const identifierInvoices = (allInvoices || []).filter(inv =>
      String(inv.awbNumber || inv.awb_number || inv.blNumber || inv.bl_number || '').trim() === currentIdentifier
    );

    // Gather invoice_data entries for this identifier
    const identifierEntries = identifierInvoices.flatMap(inv => {
      const data = inv.invoiceData || inv.invoice_data || [];
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      return Array.isArray(parsed) ? parsed : [];
    });

    // Deduplicate by invoiceNumber
    const seen = new Set();
    const uniqueEntries = identifierEntries.filter(e => {
      if (seen.has(e.invoiceNumber)) return false;
      seen.add(e.invoiceNumber);
      return true;
    });

    // Set history for display (this shipment's invoices only)
    setInvoiceDataHistory(uniqueEntries);

    // For edit mode, don't overwrite invoice number — already set from existingInvoice
    if (existingInvoice) return;
    // The series API is the authority when it answered; this per-shipment
    // count is only the fallback for when it didn't.
    if (seriesMax) return;

    // Compute next invoice number for this shipment using SB date's financial year
    const fyShort = getFinancialYear();
    let maxCount = 0;
    uniqueEntries.forEach(entry => {
      const num = (entry.invoiceNumber || '');
      const match = num.match(/(\d+)$/);
      if (match) {
        const n = parseInt(match[1], 10);
        if (n > maxCount) maxCount = n;
      }
    });
    const nextInvoiceNumber = `HMR/${fyShort}/${invoiceSeriesCode}/${String(maxCount + 1).padStart(3, '0')}`;
    setFormData(prev => ({ ...prev, invoiceNumber: nextInvoiceNumber }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingInvoice, allInvoices, shipmentIdentifier, invoiceSeriesCode, seriesMax]);

  // Fetch customer list from API + auto-select if shipment has gstNo
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        if (!customerInvoice && !normalizedShipmentUserId) return;

        const toRows = (data) => (Array.isArray(data) ? data : data?.customers || data?.data || []);

        // Shipment invoice → the customers of that shipment's user (userId OR
        // usedBy). Customer invoice → every user's customers: GET /awb/invoices
        // returns only the caller's own (usedById = me), which for an
        // accountant is normally nothing, so walk the user list instead.
        let rows;
        if (customerInvoice) {
          const ids = [...new Set(customerUsers.map(u => u?.id).filter(Boolean))];
          const results = await Promise.all(ids.map(id =>
            apiCall(`${API_ENDPOINTS.INVOICE_GET_CUSTOMERS}/user/${id}`)
              .then(r => (r.ok ? r.json() : null))
              .catch(() => null)
          ));
          const own = await apiCall(API_ENDPOINTS.INVOICE_GET_CUSTOMERS)
            .then(r => (r.ok ? r.json() : null)).catch(() => null);
          const byId = new Map();
          [own, ...results].forEach(d => toRows(d).forEach(c => { if (c?.id != null) byId.set(c.id, c); }));
          rows = [...byId.values()].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
        } else {
          const response = await apiCall(`${API_ENDPOINTS.INVOICE_GET_CUSTOMERS}/user/${normalizedShipmentUserId}`);
          rows = response.ok ? toRows(await response.json()) : null;
        }

        if (rows) {
          const raw = rows.map(c => ({
            id: c.id ?? null,
            name: c.name,
            gstNo: c.gstin || c.gstNo || '',
            address: c.address || ''
          }));
          // Deduplicate by GST No — same GST se sirf 1 customer (first one wins)
          const seenGst = new Set();
          const list = raw.filter(c => {
            const gst = c.gstNo.trim().toUpperCase();
            if (gst && seenGst.has(gst)) return false;
            if (gst) seenGst.add(gst);
            return true;
          });
          setCustomerList(list);

          // Auto-select customer ONLY if shipment gstNo matches
          const shipmentGst = (shipment.gstNo || shipment.gst_no || '').trim().toUpperCase();
          
          if (shipmentGst) {
            const matched = list.find(c => (c.gstNo || '').trim().toUpperCase() === shipmentGst);
            if (matched) {
              setFormData(prev => ({
                ...prev,
                customerId: matched.id ?? null,
                customerGSTIN: matched.gstNo || '',
                customerName: matched.name || '',
                customerAddress: matched.address || ''
              }));
              setShipperName(matched.name || '');
              setCustomerSearch('');
            }
          }

          // NOTE: Do NOT overwrite invoiceNumber/invoiceDate here.
          // invoiceNumber is HMR auto-generated (e.g., HMR/2025-26/001).
          // shipment.invoiceNumber is the exporter's SB invoice — used as Exp Inv No in PDF.
        }
      } catch (err) {
        console.error('Failed to fetch customers:', err);
      }
    };
    fetchCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedShipmentUserId, shipment.gstNo, shipment.gst_no, customerInvoice, customerUsers.length]);

  // Close customer dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(e.target)) {
        setCustomerDropdownOpen(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(e.target)) {
        setDatePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectCustomer = (customer) => {
    setFormData(prev => ({
      ...prev,
      customerId: customer.id ?? null,
      customerGSTIN: customer.gstNo || '',
      customerName: customer.name || '',
      customerAddress: customer.address || ''
    }));
    setCustomerError(false);
    setCustomerSearch('');
    setCustomerDropdownOpen(false);
  };

  const handleAddCustomer = async () => {
    if (!newCustomer.name.trim()) { alert('Customer name is required'); return; }

    // Check duplicate GST in existing list
    if (newCustomer.gstNo.trim()) {
      const duplicate = customerList.find(
        c => c.gstNo && c.gstNo.trim().toUpperCase() === newCustomer.gstNo.trim().toUpperCase()
      );
      if (duplicate) {
        alert(`GST No "${newCustomer.gstNo.trim().toUpperCase()}" already exists for customer "${duplicate.name}". Duplicate GST not allowed.`);
        return;
      }
    }

    setAddingCustomer(true);
    try {
      // If user already has billing data (customerList not empty) → send usedById
      // If user has no billing data → send userId
      const requestBody = customerList.length > 0 
        ? {
            usedById: normalizedShipmentUserId,
            name: newCustomer.name.trim(),
            address: newCustomer.address.trim(),
            gstin: newCustomer.gstNo.trim()
          }
        : {
            userId: normalizedShipmentUserId,
            name: newCustomer.name.trim(),
            address: newCustomer.address.trim(),
            gstin: newCustomer.gstNo.trim()
          };
      
      const response = await apiCall(API_ENDPOINTS.INVOICE_ADD_CUSTOMER, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      });
      if (response.ok) {
        const saved = await response.json();
        const customerObj = {
          id: saved.id ?? saved.data?.id ?? null,
          name: saved.name || newCustomer.name,
          gstNo: saved.gstin || newCustomer.gstNo,
          address: saved.address || newCustomer.address
        };
        setCustomerList(prev => [...prev, customerObj]);
        handleSelectCustomer(customerObj);
        setShowAddCustomer(false);
        setNewCustomer({ name: '', gstNo: '', address: '' });
      } else {
        const err = await response.json().catch(() => ({}));
        const msg = err.message || '';
        if (msg.toLowerCase().includes('duplicate') || msg.toLowerCase().includes('already') || msg.toLowerCase().includes('exist') || response.status === 409) {
          alert(`Duplicate entry: A customer with this GST No already exists.`);
        } else {
          alert(msg || 'Failed to add customer. Please try again.');
        }
      }
    } catch (err) {
      console.error('Add customer error:', err);
      alert('Failed to add customer. Please try again.');
    } finally {
      setAddingCustomer(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLineItemChange = (index, field, value) => {
    const updatedItems = [...formData.lineItems];
    updatedItems[index][field] = value;

    // Auto-fill HSN and GST when description is selected
    if (field === 'description') {
      const selectedOption = descriptionOptions.find(opt => opt.value === value);
      if (selectedOption) {
        updatedItems[index].hsn = selectedOption.hsn;
        updatedItems[index].gst = selectedOption.gst;
      }
      // Auto-fill Qty with chargeable weight when AIR FREIGHT is selected
      if (value === 'air_freight') {
        const chw = shipment.chWeight || shipment.ch_weight || shipment.chargeableWeight || '';
        if (chw) {
          updatedItems[index].qty = toWholeQty(chw);
        }
      }
    }

    setFormData(prev => ({
      ...prev,
      lineItems: updatedItems
    }));
  };

  const addLineItem = () => {
    const lastIdx = formData.lineItems.length - 1;
    const last = formData.lineItems[lastIdx];
    const errors = {};
    if (!last.description) errors.description = 'Description is required';
    if (!last.rate || parseFloat(last.rate) <= 0) errors.rate = 'Rate is required';
    if (!last.qty || parseFloat(last.qty) <= 0) errors.qty = 'Qty is required';
    if (Object.keys(errors).length > 0) {
      setLineItemErrors(prev => ({ ...prev, [lastIdx]: errors }));
      return;
    }
    setLineItemErrors(prev => { const n = { ...prev }; delete n[lastIdx]; return n; });
    setFormData(prev => ({
      ...prev,
      lineItems: [
        ...prev.lineItems,
        { description: '', hsn: '', rate: '', qty: '1', gst: '' }
      ]
    }));
  };

  // The invoice is the added rows plus the shipment's own charges, so an
  // untouched row is not a line anybody meant to bill. A new row is born with
  // a qty of 1, which is why qty alone does not count as filled in.
  const isBlankItem = (item) => !String(item?.description || '').trim()
    && !String(item?.hsn || '').trim()
    && !(parseFloat(String(item?.rate ?? '').replace(/,/g, '')) > 0);

  // Filter autoItems to exclude removed ones
  const visibleAutoItems = autoItems.filter((_, i) => !removedAutoItems.includes(i));

  const removeLineItem = (index) => {
    // Only the last row is protected, and only when the shipment brings
    // nothing of its own — otherwise there would be no invoice at all.
    if (formData.lineItems.length === 1 && visibleAutoItems.length === 0) {
      toast.error('An invoice needs at least one charge');
      return;
    }
    setFormData(prev => ({
      ...prev,
      lineItems: prev.lineItems.filter((_, i) => i !== index)
    }));
  };

  const dragItem = React.useRef(null);
  const dragOverItem = React.useRef(null);

  const handleDragStart = (index) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = () => {
    const from = dragItem.current;
    const to = dragOverItem.current;
    if (from === null || to === null || from === to) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }
    const items = [...formData.lineItems];
    const [moved] = items.splice(from, 1);
    items.splice(to, 0, moved);
    setFormData(prev => ({ ...prev, lineItems: items }));
    setLineItemErrors({});
    dragItem.current = null;
    dragOverItem.current = null;
  };

  // Every issued number, mapped to whoever holds it. Built once per invoice
  // list rather than per keystroke — live carries thousands of invoices, and
  // re-scanning them on every character typed into the number field is enough
  // to make the field feel stuck.
  const invoiceNumberOwners = useMemo(() => {
    const owners = new Map();
    for (const inv of allInvoices || []) {
      // Air invoices carry invoice_data as its own column; sea invoices only
      // have it nested inside the `invoice` JSON, so read both.
      const entries = [inv?.invoiceData, inv?.invoice_data, inv?.invoice?.invoice_data].flatMap((raw) => {
        let parsed = raw;
        if (typeof raw === 'string') { try { parsed = JSON.parse(raw); } catch { parsed = []; } }
        return Array.isArray(parsed) ? parsed : [];
      });
      if (!entries.length) continue;

      const identifier = String(inv?.awbNumber || inv?.awb_number || inv?.blNumber || inv?.bl_number || '').trim();
      const buyer = inv?.buyer && typeof inv.buyer === 'object' ? inv.buyer : null;
      const owner = {
        recordId: String(inv?.id ?? inv?.invoiceId ?? ''),
        customerId: String(inv?.customerId ?? inv?.customer_id ?? ''),
        identifier,
        isSea: Boolean(inv?.blNumber || inv?.bl_number),
        customerName: buyer?.name || buyer?.customerName || inv?.customerName || '',
      };
      for (const e of entries) {
        const num = String(e?.invoiceNumber || '').trim().toUpperCase();
        if (num && !owners.has(num)) owners.set(num, owner);
      }
    }
    return owners;
  }, [allInvoices]);

  // An invoice number belongs to exactly one shipment (or one customer). The
  // number can be typed by hand, so check before letting it through and name
  // whoever already holds it.
  const invoiceNumberClash = useMemo(() => {
    const wanted = String(formData.invoiceNumber || '').trim().toUpperCase();
    if (!wanted) return null;

    const owner = invoiceNumberOwners.get(wanted);
    if (!owner) return null;

    const ownId = String(
      normalizedExistingInvoice?.id ?? normalizedExistingInvoice?.invoice?.id ?? normalizedExistingInvoice?.invoiceId ?? ''
    );
    // Re-issuing a number on the record, shipment or customer that already
    // owns it is an edit, not a clash.
    if (ownId && owner.recordId === ownId) return null;
    if (owner.identifier && owner.identifier === shipmentIdentifier) return null;
    if (!owner.identifier && formData.customerId != null && owner.customerId === String(formData.customerId)) return null;

    return owner;
  }, [invoiceNumberOwners, formData.invoiceNumber, formData.customerId, normalizedExistingInvoice, shipmentIdentifier]);

  // Numbering can leave gaps — a number was skipped, or an invoice was deleted.
  // Typing one of those is allowed, so confirm it is free rather than leaving
  // the accountant to guess from the absence of an error.
  const invoiceNumberBackfill = useMemo(() => {
    if (invoiceNumberClash || existingInvoice || !seriesMax) return null;
    const typed = String(formData.invoiceNumber || '').trim();
    if (!typed.startsWith(`${seriesPrefix}/`)) return null;
    const m = typed.match(/(\d+)$/);
    if (!m) return null;
    const n = parseInt(m[1], 10);
    if (!Number.isFinite(n) || n <= 0 || n > seriesMax.count) return null;
    return m[1];
  }, [invoiceNumberClash, existingInvoice, seriesMax, seriesPrefix, formData.invoiceNumber]);

  const invoiceNumberClashText = invoiceNumberClash && [
    'This invoice number is already assigned to',
    invoiceNumberClash.identifier
      ? `${invoiceNumberClash.isSea ? 'B/L' : 'AWB'} ${invoiceNumberClash.identifier}`
      : 'another customer invoice',
    invoiceNumberClash.customerName ? `(${invoiceNumberClash.customerName})` : '',
  ].filter(Boolean).join(' ') + '. Use a different number.';

  // Preview sits in the aside, so whatever is wrong is usually off screen when
  // it is pressed. Marking the field red on its own reads as a dead button —
  // say what is missing and take them to it.
  const revealProblem = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusable = el.matches('input, button') ? el : el.querySelector('input, button');
    window.setTimeout(() => focusable?.focus?.({ preventScroll: true }), 350);
  };

  const validateForm = () => {
    if (!formData.customerName) {
      setCustomerError(true);
      toast.error('Pick the customer this invoice is billed to');
      revealProblem('inv-customer');
      return false;
    }
    if (invoiceNumberClash) {
      toast.error(invoiceNumberClashText);
      revealProblem('inv-number');
      return false;
    }

    // Rows left untouched are dropped rather than held against the user, as
    // long as something else on the invoice is billable.
    const billable = formData.lineItems.filter((item) => !isBlankItem(item));
    if (billable.length === 0 && visibleAutoItems.length === 0) {
      toast.error('Add at least one charge before previewing');
      revealProblem('inv-item-0');
      return false;
    }
    if (billable.length !== formData.lineItems.length) {
      setFormData((prev) => ({ ...prev, lineItems: billable }));
      setLineItemErrors({});
      // The dropped rows leave the kept ones at new indexes, so this pass
      // validates what is left on the next render instead of mid-change.
      return billable.every((item) => item.description
        && parseFloat(String(item.rate).replace(/,/g, '')) > 0
        && parseFloat(String(item.qty).replace(/,/g, '')) > 0);
    }

    const found = {};
    let firstBad = null;
    formData.lineItems.forEach((item, i) => {
      const errors = {};
      if (!item.description) errors.description = 'Description is required';
      if (!item.rate || parseFloat(String(item.rate).replace(/,/g,'')) <= 0) errors.rate = 'Rate is required';
      if (!item.qty || parseFloat(String(item.qty).replace(/,/g,'')) <= 0) errors.qty = 'Qty is required';
      if (Object.keys(errors).length > 0) {
        found[i] = errors;
        if (firstBad === null) firstBad = i;
      }
    });
    // Replacing the map rather than merging clears the rows since put right.
    setLineItemErrors(found);
    if (firstBad === null) return true;

    const missing = found[firstBad];
    const what = ['description', 'rate', 'qty'].filter((k) => missing[k]);
    toast.error(`Item ${firstBad + 1} still needs its ${what.join(', ').replace(/, ([^,]*)$/, ' and $1')}`);
    revealProblem(`inv-item-${firstBad}`);
    return false;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setShowPreview(true);
    }
  };

  // What the invoice adds up to as it is being filled in — the summary beside
  // the form reads this, so a reviewer sees the figure before Preview.
  const runningTotals = React.useMemo(() => {
    const intra = String(formData.customerGSTIN || '').startsWith('07');
    let taxable = 0;
    let tax = 0;
    let counted = 0;
    formData.lineItems.forEach((item) => {
      const rate = parseFloat(String(item.rate ?? '').replace(/,/g, '')) || 0;
      const qty = Math.round(parseFloat(String(item.qty ?? '').replace(/,/g, '')) || 0);
      if (!rate || !qty) return;
      const amount = Math.ceil(rate * qty);
      taxable += amount;
      tax += Math.ceil((amount * (parseFloat(item.gst) || 18)) / 100);
      counted += 1;
    });
    return { intra, taxable, tax, grand: taxable + tax, counted };
  }, [formData.lineItems, formData.customerGSTIN]);

  const money = (v) => `₹${(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  if (showPreview) {
    return (
      <InvoicePreview 
        formData={{ ...formData, lineItems: [...formData.lineItems, ...visibleAutoItems] }} 
        shipment={{ ...shipment, userId: normalizedShipmentUserId, userID: normalizedShipmentUserId, user_id: normalizedShipmentUserId }}
        customerInvoice={customerInvoice} 
        bankDetails={bankDetails[formData.bankName]}
        bankName={formData.bankName}
        shipperName={shipperName || formData.customerName}
        existingInvoiceId={normalizedExistingInvoice?.id || normalizedExistingInvoice?.invoice?.id || normalizedExistingInvoice?.invoiceId || null}
        allInvoices={allInvoices}
        onClose={onClose}
        onBack={() => setShowPreview(false)}
        onSubmitSuccess={onClose}
      />
    );
  }

  return (
    <>
    <div className="min-h-screen bg-[#f4f7fc] dark:bg-[#080d19]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="rounded-2xl shadow-lg mb-5 overflow-hidden
                        bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600">
          <div className="px-5 sm:px-6 py-5 relative">
            <div className="flex items-start gap-4">
              <span className="grid place-items-center w-12 h-12 rounded-2xl flex-shrink-0
                               bg-white/15 ring-1 ring-white/25 text-white">
                <FileText size={22} />
              </span>

              <div className="min-w-0 flex-1">
                <p className="text-[10.5px] font-black uppercase tracking-[0.14em] text-white/60">
                  Billing · {customerInvoice ? 'Customer invoice' : 'Shipment invoice'}
                </p>
                <h1 className="text-[26px] sm:text-[30px] leading-tight font-black text-white">
                  {existingInvoice ? 'Edit Invoice' : 'Generate Invoice'}
                </h1>

                {customerInvoice ? (
                  <p className="text-[13px] text-white/70 mt-1.5">
                    Not tied to a shipment — billed straight to the customer you pick below.
                  </p>
                ) : (
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    {[
                      [Barcode, shipmentIdentifierLabel, shipmentIdentifier],
                      [Building2, 'Consignee', shipment.customerName],
                      [Truck, 'Shipper', shipperName || formData.customerName],
                      [FileText, 'SB No', shipment.sbNumber || shipment.sb_number],
                      [MapPin, 'POL', shipment.origin || shipment.cityCode],
                      [MapPin, 'Destination', shipment.destCity],
                      [MapPin, 'POD', shipment.cityCode],
                      [Scale, 'Ch. Wt', shipment.chWeight && shipment.chWeight !== '-' ? `${shipment.chWeight} KG` : null],
                      [Boxes, 'PKG', shipment.totalPkt && shipment.totalPkt !== '-' ? `${shipment.totalPkt} NOS` : null],
                      [Calendar, 'S/B Date', formatShipmentDate(shipment.sbDate || shipment.sb_date || shipment.sbDates)],
                      [Calendar, 'LEO Date', formatShipmentDate(shipment.leoDate || shipment.leo_date)],
                    ].filter(([, , value]) => value).map(([Icon, label, value]) => (
                      <span key={label} className="inline-flex items-center gap-2 h-8 pl-2 pr-1 rounded-xl
                                                   bg-white/10 ring-1 ring-white/15">
                        <Icon size={13} className="text-white/70 flex-shrink-0" />
                        <span className="text-[11px] font-bold text-white/70 whitespace-nowrap">{label}:</span>
                        <span className="px-2 py-1 rounded-lg bg-white/90 text-[11.5px] font-black text-slate-800
                                         max-w-[260px] truncate" title={String(value)}>
                          {value}
                        </span>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={onClose}
                className="p-2 rounded-xl text-white/70 hover:text-white hover:bg-white/15 transition flex-shrink-0"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="grid lg:grid-cols-[minmax(0,1fr)_340px] gap-5 items-start">
        <div className="min-w-0 space-y-5">
          {/* ── 1. who it is billed to ── */}
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm
                              dark:border-slate-800 dark:bg-[#0e1626] dark:shadow-none">
            <div className="flex items-center justify-between gap-3 px-5 py-3.5 rounded-t-2xl
                            border-b border-slate-100 dark:border-slate-800
                            bg-gradient-to-r from-blue-50/80 to-transparent dark:from-blue-500/[0.07]">
              <div className="flex items-center gap-3 min-w-0">
                <span className="grid place-items-center w-8 h-8 rounded-xl text-[12px] font-black flex-shrink-0
                                 bg-blue-600 text-white">1</span>
                <div className="min-w-0">
                  <h3 className="text-[14px] font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <User size={15} className="text-blue-600 dark:text-blue-400" /> Customer details
                  </h3>
                  <p className="text-[11.5px] text-slate-500 dark:text-slate-400">The party this invoice is billed to.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowAddCustomer(true)}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl text-[12.5px] font-bold text-white
                           bg-blue-600 hover:bg-blue-700 transition flex-shrink-0"
              >
                <UserPlus size={14} /> Add new
              </button>
            </div>
            <div className="p-5">

            {/* Customer Search Dropdown */}
            <div className="relative" id="inv-customer" ref={customerDropdownRef}>
              <label className="block text-[12px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                Select Customer <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                className={`h-11 w-full rounded-xl px-3.5 text-sm bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700/70 text-slate-800 dark:text-slate-100 outline-none transition cursor-pointer flex items-center justify-between gap-2 font-semibold
                            ${customerDropdownOpen ? 'border-blue-400 ring-4 ring-blue-500/10' : ''}
                            ${customerError ? 'border-rose-400 ring-4 ring-rose-500/10' : ''}`}
                onClick={() => setCustomerDropdownOpen(prev => !prev)}
              >
                <span className="inline-flex items-center gap-2 min-w-0">
                  <User size={15} className="text-slate-400 flex-shrink-0" />
                  <span className={`truncate ${formData.customerName ? '' : 'text-slate-400 font-normal'}`}>
                    {formData.customerName || 'Search and select a customer…'}
                  </span>
                </span>
                <ChevronDown size={15} className={`text-slate-400 flex-shrink-0 transition ${customerDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {customerError && (
                <p className="mt-1.5 text-[11.5px] font-semibold text-rose-500">Customer is required</p>
              )}

              {customerDropdownOpen && (
                <div className="absolute z-50 mt-2 w-full rounded-xl overflow-hidden shadow-2xl
                                border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                  {/* Search */}
                  <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100 dark:border-slate-800">
                    <Search size={14} className="text-slate-400 flex-shrink-0" />
                    <input
                      autoFocus
                      type="text"
                      placeholder="Search customer..."
                      value={customerSearch}
                      onChange={e => { setCustomerSearch(e.target.value); setCustomerHighlightIdx(-1); }}
                      onKeyDown={e => {
                        const filtered = customerList.filter(c => c.name?.toLowerCase().includes(customerSearch.toLowerCase()));
                        if (e.key === 'ArrowDown') { e.preventDefault(); setCustomerHighlightIdx(prev => Math.min(filtered.length - 1, prev + 1)); }
                        else if (e.key === 'ArrowUp') { e.preventDefault(); setCustomerHighlightIdx(prev => Math.max(0, prev - 1)); }
                        else if (e.key === 'Enter' && customerHighlightIdx >= 0 && filtered[customerHighlightIdx]) { e.preventDefault(); handleSelectCustomer(filtered[customerHighlightIdx]); }
                        else if (e.key === 'Escape') { setCustomerDropdownOpen(false); }
                      }}
                      className="w-full text-[13px] bg-transparent outline-none text-slate-800 dark:text-slate-100 placeholder-slate-400"
                    />
                    {customerSearch && (
                      <button type="button" onClick={() => setCustomerSearch('')}>
                        <X size={14} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200" />
                      </button>
                    )}
                  </div>
                  {/* Options */}
                  <ul className="max-h-52 overflow-y-auto">
                    {customerList
                      .filter(c => c.name?.toLowerCase().includes(customerSearch.toLowerCase()))
                      .map((customer, idx) => (
                        <li
                          key={idx}
                          onMouseDown={() => handleSelectCustomer(customer)}
                          onMouseEnter={() => setCustomerHighlightIdx(idx)}
                          className={`px-3.5 py-2.5 cursor-pointer transition ${
                            customerHighlightIdx === idx || formData.customerName === customer.name
                              ? 'bg-blue-50 dark:bg-blue-500/15'
                              : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                          }`}
                        >
                          <p className={`text-[13px] font-bold truncate ${
                            formData.customerName === customer.name
                              ? 'text-blue-700 dark:text-blue-300'
                              : 'text-slate-800 dark:text-slate-100'}`}>{customer.name}</p>
                          <p className="text-[11.5px] text-slate-400 dark:text-slate-500 truncate">
                            {[customer.gstNo, customer.address].filter(Boolean).join(' · ')}
                          </p>
                        </li>
                      ))}
                    {customerList.filter(c => c.name?.toLowerCase().includes(customerSearch.toLowerCase())).length === 0 && (
                      <li className="px-3.5 py-6 text-[13px] text-slate-400 text-center">
                        No customers found.
                        <button
                          type="button"
                          className="ml-1 font-bold text-blue-600 dark:text-blue-400 hover:underline"
                          onMouseDown={() => { setCustomerDropdownOpen(false); setShowAddCustomer(true); }}
                        >
                          Add new?
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            {/* Selected Customer Info */}
            {formData.customerName && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="rounded-xl px-3.5 py-2.5 border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/50">
                  <p className="text-[9.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Customer Name</p>
                  <p className="text-[13px] font-bold text-slate-800 dark:text-slate-100 mt-0.5">{formData.customerName}</p>
                </div>
                <div className="rounded-xl px-3.5 py-2.5 border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/50">
                  <p className="text-[9.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">GST No</p>
                  <p className="text-[13px] font-bold text-slate-800 dark:text-slate-100 font-mono mt-0.5">{formData.customerGSTIN || '—'}</p>
                </div>
                <div className="rounded-xl px-3.5 py-2.5 border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900/50">
                  <p className="text-[9.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Address</p>
                  <p className="text-[12.5px] font-bold text-slate-800 dark:text-slate-100 mt-0.5">{formData.customerAddress || '—'}</p>
                </div>
              </div>
            )}
            </div>
          </section>

          {/* ── 2. the invoice's own particulars ── */}
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm
                              dark:border-slate-800 dark:bg-[#0e1626] dark:shadow-none">
            <div className="flex items-center justify-between gap-3 px-5 py-3.5 rounded-t-2xl
                            border-b border-slate-100 dark:border-slate-800
                            bg-gradient-to-r from-violet-50/80 to-transparent dark:from-violet-500/[0.07]">
              <div className="flex items-center gap-3 min-w-0">
                <span className="grid place-items-center w-8 h-8 rounded-xl text-[12px] font-black flex-shrink-0
                                 bg-violet-600 text-white">2</span>
                <div className="min-w-0">
                  <h3 className="text-[14px] font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <FileText size={15} className="text-violet-600 dark:text-violet-400" /> Invoice details
                  </h3>
                  <p className="text-[11.5px] text-slate-500 dark:text-slate-400">Number, date and how it is taxed.</p>
                </div>
              </div>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Invoice Number - Auto / Manual */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-[12px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                  Invoice Number
                </label>
                <button
                  type="button"
                  onClick={() => {
                    if (invoiceManualMode && seriesMax) {
                      // switching back to auto → continue the series
                      setFormData(prev => ({ ...prev, invoiceNumber: nextInSeries(seriesMax.count) }));
                    } else if (invoiceManualMode) {
                      // no series answer — fall back to this shipment's own invoices
                      const currentIdentifier = shipmentIdentifier;
                      const identifierInvoices = (allInvoices || []).filter(inv =>
                        String(inv.awbNumber || inv.awb_number || inv.blNumber || inv.bl_number || '').trim() === currentIdentifier
                      );
                      const identifierEntries = identifierInvoices.flatMap(inv => {
                        const data = inv.invoiceData || inv.invoice_data || [];
                        const parsed = typeof data === 'string' ? JSON.parse(data) : data;
                        return Array.isArray(parsed) ? parsed : [];
                      });
                      // Use SB date's financial year
                      const fyShort = getFinancialYear();
                      let maxCount = 0;
                      identifierEntries.forEach(entry => {
                        const match = (entry.invoiceNumber || '').match(/(\d+)$/);
                        if (match) { const n = parseInt(match[1], 10); if (n > maxCount) maxCount = n; }
                      });
                      const nextNum = `HMR/${fyShort}/${invoiceSeriesCode}/${String(maxCount + 1).padStart(3, '0')}`;
                      setFormData(prev => ({ ...prev, invoiceNumber: nextNum }));
                    }
                    setInvoiceManualMode(prev => !prev);
                  }}
                  className={`text-xs font-medium px-2.5 py-1 rounded-full transition ${
                    invoiceManualMode
                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 hover:bg-amber-200'
                      : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 hover:bg-blue-200'
                  }`}
                >
                  {invoiceManualMode ? '↺ Auto' : '✎ Manual'}
                </button>
              </div>
              <div className="relative" id="inv-number">
                {invoiceManualMode ? (
                  <div className={`h-11 flex items-stretch rounded-xl overflow-hidden border transition ${
                    invoiceNumberClash
                      ? 'border-rose-300 dark:border-rose-500/60 focus-within:ring-4 focus-within:ring-rose-500/10'
                      : 'border-amber-300 dark:border-amber-500/60 focus-within:ring-4 focus-within:ring-amber-500/10'
                  }`}>
                    <span className="inline-flex items-center px-3 select-none whitespace-nowrap text-[13px] font-mono font-bold
                                     bg-slate-100 text-slate-500 border-r border-slate-200
                                     dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700">
                      {getInvoicePrefix(formData.invoiceNumber)}
                    </span>
                    <input
                      type="text"
                      value={getInvoiceSuffix(formData.invoiceNumber)}
                      onChange={(e) => {
                        invoiceNumberTouchedRef.current = true;
                        const prefix = getInvoicePrefix(formData.invoiceNumber);
                        setFormData(prev => ({ ...prev, invoiceNumber: prefix + e.target.value }));
                      }}
                      className="flex-1 px-3.5 min-w-0 bg-slate-50 dark:bg-slate-800/70 text-[13px] font-mono font-bold text-amber-600 dark:text-amber-400 outline-none"
                      placeholder="001"
                    />
                  </div>
                ) : (
                  <div className="h-11 w-full rounded-xl px-3.5 text-sm border border-slate-200 bg-slate-100 text-slate-700 flex items-center justify-between gap-2 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
                    <span className="font-mono font-bold text-blue-700 dark:text-blue-400 text-sm tracking-wide">
                      {formData.invoiceNumber}
                    </span>
                    <span className="px-2 py-0.5 rounded-full whitespace-nowrap text-[9.5px] font-black uppercase tracking-[0.12em]
                                     bg-white text-slate-400 border border-slate-200
                                     dark:bg-slate-900 dark:text-slate-500 dark:border-slate-700">Auto</span>
                  </div>
                )}
                {invoiceNumberClash && (
                  <p className="mt-2 flex items-start gap-1.5 text-xs font-semibold text-red-600 dark:text-red-400">
                    <AlertTriangle size={14} className="flex-shrink-0 mt-px" />
                    <span>{invoiceNumberClashText}</span>
                  </p>
                )}
                {invoiceNumberBackfill && (
                  <p className="mt-2 flex items-start gap-1.5 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                    <CheckCircle2 size={14} className="flex-shrink-0 mt-px" />
                    <span>
                      <span className="font-mono">{invoiceNumberBackfill}</span> is free — no invoice uses it. The
                      series stays at <span className="font-mono">{String(seriesMax.count).padStart(3, '0')}</span>.
                    </span>
                  </p>
                )}
                {/* What the series is standing at, straight from the API — so it
                    is obvious which number this one continues from. */}
                {!existingInvoice && !invoiceNumberClash && !invoiceNumberBackfill && (
                  <p className="mt-2 text-[11.5px] text-slate-500 dark:text-slate-400">
                    {seriesMaxNumber ? (
                      <>
                        Last generated:{' '}
                        <span className="font-mono font-bold text-slate-700 dark:text-slate-200">{seriesMaxNumber}</span>
                        {' '}· next{' '}
                        <span className="font-mono font-semibold text-blue-700 dark:text-blue-400">
                          {String(seriesMax.count + 1).padStart(3, '0')}
                        </span>
                      </>
                    ) : seriesChecked ? (
                      <>No invoice yet in <span className="font-mono">{seriesPrefix}</span> — this one starts the series at <span className="font-mono font-semibold">001</span>.</>
                    ) : (
                      'Checking the last invoice number…'
                    )}
                  </p>
                )}

                {/* Recent History from API invoice_data */}
                {(() => {
                  if (invoiceDataHistory.length === 0) return null;
                  const recentItems = invoiceDataHistory.slice(0, 5);
                  return (
                    <div className="mt-2">
                      <p className="mb-1.5 text-[9.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">Recent invoices</p>
                      <div className="flex flex-wrap gap-1.5">
                        {recentItems.map((h, i) => (
                          <span key={i} className="px-2 py-0.5 rounded-full font-mono text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:border-blue-500/25" title={h.invoiceDate}>
                            {h.invoiceNumber}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Invoice Date - Custom Picker */}
            <div>
              <label className="block text-[12px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                Invoice Date <span className="text-red-500">*</span>
              </label>
              <div className="relative" ref={datePickerRef}>
                {/* Trigger */}
                <button
                  type="button"
                  onClick={() => {
                    const [y, m, d] = formData.invoiceDate.split('-').map(Number);
                    setCalViewDate(new Date(y, m - 1, 1));
                    setDatePickerOpen(prev => !prev);
                  }}
                  className="h-11 w-full rounded-xl px-3.5 text-sm bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700/70 text-slate-800 dark:text-slate-100 placeholder-slate-400 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition flex items-center justify-between gap-3"
                >
                  <span className="flex items-center gap-2">
                    <Calendar size={16} className="text-blue-500 flex-shrink-0" />
                    <span className="font-medium">
                      {(() => {
                        const [y, m, d] = formData.invoiceDate.split('-').map(Number);
                        return new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
                      })()}
                    </span>
                  </span>
                  <ChevronDown size={15} className={`text-slate-400 transition-transform ${datePickerOpen ? 'rotate-180' : ''}`} />
                </button>

                {/* Calendar Dropdown */}
                {datePickerOpen && (() => {
                  const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
                  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
                  const viewYear = calViewDate.getFullYear();
                  const viewMonth = calViewDate.getMonth();
                  const [selY, selM, selD] = formData.invoiceDate.split('-').map(Number);
                  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
                  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
                  const daysInPrev = new Date(viewYear, viewMonth, 0).getDate();
                  const today = new Date();

                  const cells = [];
                  // previous month days
                  for (let i = firstDay - 1; i >= 0; i--) cells.push({ day: daysInPrev - i, month: 'prev' });
                  // current month days
                  for (let i = 1; i <= daysInMonth; i++) cells.push({ day: i, month: 'cur' });
                  // next month filler
                  const remaining = 42 - cells.length;
                  for (let i = 1; i <= remaining; i++) cells.push({ day: i, month: 'next' });

                  const isSelected = (cell) => cell.month === 'cur' && cell.day === selD && viewMonth === selM - 1 && viewYear === selY;
                  const isToday = (cell) => cell.month === 'cur' && cell.day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();

                  return (
                    <div className="absolute z-50 mt-2 w-72 p-4 rounded-xl shadow-2xl
                                    border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                      {/* Month/Year Nav */}
                      <div className="flex items-center justify-between mb-4">
                        <button type="button"
                          onClick={() => setCalViewDate(new Date(viewYear, viewMonth - 1, 1))}
                          className="h-7 w-7 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition">
                          <ChevronLeft size={15} />
                        </button>
                        <div className="flex items-center gap-2">
                          <select value={viewMonth}
                            onChange={e => setCalViewDate(new Date(viewYear, parseInt(e.target.value), 1))}
                            className="ql-select ql-select--bare text-[13px] font-bold text-slate-800 dark:text-slate-100 bg-transparent outline-none cursor-pointer">
                            {MONTHS.map((m, i) => <option key={i} value={i}>{m}</option>)}
                          </select>
                          <select value={viewYear}
                            onChange={e => setCalViewDate(new Date(parseInt(e.target.value), viewMonth, 1))}
                            className="ql-select ql-select--bare text-[13px] font-bold text-slate-800 dark:text-slate-100 bg-transparent outline-none cursor-pointer">
                            {Array.from({length: 10}, (_, i) => today.getFullYear() - 2 + i).map(y => <option key={y} value={y}>{y}</option>)}
                          </select>
                        </div>
                        <button type="button"
                          onClick={() => setCalViewDate(new Date(viewYear, viewMonth + 1, 1))}
                          className="h-7 w-7 grid place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition">
                          <ChevronRight size={15} />
                        </button>
                      </div>

                      {/* Day Headers */}
                      <div className="grid grid-cols-7 mb-2">
                        {DAYS.map(d => (
                          <div key={d} className="text-center py-1 text-[9.5px] font-black uppercase tracking-[0.1em] text-slate-400 dark:text-slate-500">{d}</div>
                        ))}
                      </div>

                      {/* Day Cells */}
                      <div className="grid grid-cols-7 gap-y-1">
                        {cells.map((cell, i) => (
                          <button key={i} type="button"
                            disabled={cell.month !== 'cur'}
                            onClick={() => {
                              const mm = String(viewMonth + 1).padStart(2, '0');
                              const dd = String(cell.day).padStart(2, '0');
                              handleInputChange('invoiceDate', `${viewYear}-${mm}-${dd}`);
                              setDatePickerOpen(false);
                            }}
                            className={`h-8 w-8 mx-auto rounded-lg text-[12.5px] font-semibold transition flex items-center justify-center
                              ${cell.month !== 'cur' ? 'text-slate-300 dark:text-slate-700 cursor-default' : ''}
                              ${isSelected(cell) ? 'bg-blue-600 text-white font-bold shadow-lg shadow-blue-500/25' : ''}
                              ${isToday(cell) && !isSelected(cell) ? 'ring-1 ring-blue-400 text-blue-600 dark:text-blue-400' : ''}
                              ${cell.month === 'cur' && !isSelected(cell) ? 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800 cursor-pointer' : ''}
                            `}
                          >
                            {cell.day}
                          </button>
                        ))}
                      </div>

                      {/* Today Button */}
                      <div className="mt-3 pt-3 border-t border-slate-100 dark:border-slate-800 flex justify-center">
                        <button type="button"
                          onClick={() => {
                            const t = new Date();
                            const mm = String(t.getMonth() + 1).padStart(2, '0');
                            const dd = String(t.getDate()).padStart(2, '0');
                            handleInputChange('invoiceDate', `${t.getFullYear()}-${mm}-${dd}`);
                            setCalViewDate(new Date(t.getFullYear(), t.getMonth(), 1));
                            setDatePickerOpen(false);
                          }}
                          className="text-[11.5px] font-bold text-blue-600 dark:text-blue-400 hover:underline transition"
                        >
                          Today
                        </button>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Supply Type */}
            <div>
              <label className="block text-[12px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                Supply Type <span className="text-red-500">*</span>
              </label>
              <Select
                clearable={false}
                value={formData.supplyType}
                onChange={(v) => handleInputChange('supplyType', v)}
                options={[
                  { value: 'B2C', label: 'B2C (Business to Consumer)' },
                  { value: 'B2B', label: 'B2B (Business to Business)' },
                ]}
              />
            </div>

            {/* Bank Name */}
            <div>
              <label className="block text-[12px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                Bank Name <span className="text-red-500">*</span>
              </label>
              <Select
                clearable={false}
                value={formData.bankName}
                onChange={(v) => handleInputChange('bankName', v)}
                options={[
                  { value: 'ICICI', label: 'ICICI Bank' },
                  { value: 'HDFC', label: 'HDFC Bank' },
                ]}
              />
            </div>

            </div>
          </section>

          {/* ── 3. what is being charged ── */}
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm
                              dark:border-slate-800 dark:bg-[#0e1626] dark:shadow-none">
            <div className="flex items-center justify-between gap-3 px-5 py-3.5 rounded-t-2xl
                            border-b border-slate-100 dark:border-slate-800
                            bg-gradient-to-r from-emerald-50/80 to-transparent dark:from-emerald-500/[0.07]">
              <div className="flex items-center gap-3 min-w-0">
                <span className="grid place-items-center w-8 h-8 rounded-xl text-[12px] font-black flex-shrink-0
                                 bg-emerald-600 text-white">3</span>
                <div className="min-w-0">
                  <h3 className="text-[14px] font-bold text-slate-900 dark:text-white flex items-center gap-2">
                    <Package size={15} className="text-emerald-600 dark:text-emerald-400" /> Line items
                  </h3>
                  <p className="text-[11.5px] text-slate-500 dark:text-slate-400">Each charge, its HSC code and its rate.</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {runningTotals.counted > 0 && (
                  <span className="px-2.5 py-1 rounded-full text-[11px] font-bold
                                   bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                    {runningTotals.counted} priced
                  </span>
                )}
                <button
                  type="button"
                  onClick={addLineItem}
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl text-[12.5px] font-bold text-white
                             bg-emerald-600 hover:bg-emerald-700 transition"
                >
                  <Plus size={14} /> Add item
                </button>
              </div>
            </div>
            <div className="p-5">

            {visibleAutoItems.length > 0 && (
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[10.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500">
                  Charges you add
                </span>
                <span className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
              </div>
            )}

            <div className="space-y-4">
              {formData.lineItems.map((item, index) => (
                <div
                  key={index}
                  id={`inv-item-${index}`}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragEnter={() => handleDragEnter(index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={e => e.preventDefault()}
                  className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-800 dark:bg-slate-900/40"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span
                        className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 touch-none"
                        title="Drag to reorder"
                      >
                        <GripVertical size={18} />
                      </span>
                      <span className="inline-flex items-center gap-2.5 min-w-0">
                        <span className="grid place-items-center w-7 h-7 rounded-full text-[11px] font-black flex-shrink-0
                                         bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300">
                          {index + 1}
                        </span>
                        <span className="text-[13.5px] font-black uppercase tracking-wide truncate
                                         text-slate-900 dark:text-white">
                          {item.description
                            ? (descriptionOptions.find(o => o.value === item.description)?.label || item.description)
                            : `Item ${index + 1}`}
                        </span>
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {item.gst && (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold
                                         bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                          <Settings size={11} /> Auto · {item.gst}% GST
                        </span>
                      )}
                      {(formData.lineItems.length > 1 || visibleAutoItems.length > 0) && (
                        <button
                          type="button"
                          onClick={() => removeLineItem(index)}
                          className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50
                                     dark:text-rose-400 dark:hover:bg-rose-500/10 transition"
                          title="Remove"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                    {/* Description */}
                    <div className="md:col-span-5">
                      <label className="block text-[11.5px] font-bold text-slate-600 dark:text-slate-400 mb-1.5">
                        Description <span className="text-red-500">*</span>
                      </label>
                      <div
                        className="relative"
                        ref={el => descDropdownRef.current[index] = el}
                        onMouseDown={e => e.stopPropagation()}
                      >
                        {/* Trigger Button */}
                        <button
                          type="button"
                          onClick={() => { setLineItemErrors(prev => { const n={...prev}; if(n[index]) { delete n[index].description; if(!Object.keys(n[index]).length) delete n[index]; } return n; }); setDescDropdown(prev => ({
                            ...prev,
                            [index]: { search: prev[index]?.search || '', open: !prev[index]?.open }
                          })); }}
                          className="w-full h-11 px-3.5 text-[13px] font-semibold rounded-xl outline-none transition bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 dark:bg-slate-800/70 dark:border-slate-700 dark:text-slate-100 flex items-center justify-between"
                        >
                          <span className={item.description ? '' : 'text-slate-400 font-normal'}>
                            {item.description
                              ? descriptionOptions.find(o => o.value === item.description)?.label || item.description
                              : 'Select Description'}
                          </span>
                          <ChevronDown size={14} className="text-slate-400 flex-shrink-0" />
                        </button>

                        {/* Dropdown */}
                        {descDropdown[index]?.open && (
                          <div className="absolute z-50 mt-2 w-full rounded-xl shadow-2xl overflow-hidden
                                          border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
                            {/* Search Input */}
                            <div className="flex items-center gap-2 px-3 py-2.5 border-b border-slate-100 dark:border-slate-800">
                              <Search size={13} className="text-slate-400 flex-shrink-0" />
                              <input
                                autoFocus
                                type="text"
                                placeholder="Search..."
                                value={descDropdown[index]?.search || ''}
                                onChange={e => { setDescDropdown(prev => ({ ...prev, [index]: { ...prev[index], search: e.target.value } })); setDescHighlightIdx(prev => ({ ...prev, [index]: -1 })); }}
                                onKeyDown={e => {
                                  const filteredOpts = descriptionOptions.filter(opt => opt.label.toLowerCase().includes((descDropdown[index]?.search || '').toLowerCase()));
                                  const hi = descHighlightIdx[index] ?? -1;
                                  if (e.key === 'ArrowDown') { e.preventDefault(); setDescHighlightIdx(prev => ({ ...prev, [index]: Math.min(filteredOpts.length - 1, hi + 1) })); }
                                  else if (e.key === 'ArrowUp') { e.preventDefault(); setDescHighlightIdx(prev => ({ ...prev, [index]: Math.max(0, hi - 1) })); }
                                  else if (e.key === 'Enter' && hi >= 0 && filteredOpts[hi]) { e.preventDefault(); handleLineItemChange(index, 'description', filteredOpts[hi].value); setDescDropdown(prev => ({ ...prev, [index]: { search: '', open: false } })); }
                                  else if (e.key === 'Escape') { setDescDropdown(prev => ({ ...prev, [index]: { ...prev[index], open: false } })); }
                                }}
                                className="w-full text-[13px] bg-transparent outline-none text-slate-800 dark:text-slate-100 placeholder-slate-400"
                              />
                            </div>
                            {/* Options List */}
                            <ul className="max-h-52 overflow-y-auto">
                              {descriptionOptions
                                .filter(opt => opt.label.toLowerCase().includes((descDropdown[index]?.search || '').toLowerCase()))
                                .map(opt => (
                                  <li
                                    key={opt.value}
                                    onMouseDown={() => {
                                      handleLineItemChange(index, 'description', opt.value);
                                      setDescDropdown(prev => ({ ...prev, [index]: { search: '', open: false } }));
                                    }}
                                    onMouseEnter={() => setDescHighlightIdx(prev => ({ ...prev, [index]: descriptionOptions.filter(o => o.label.toLowerCase().includes((descDropdown[index]?.search || '').toLowerCase())).indexOf(opt) }))}
                                    className={`px-3 py-2 text-sm cursor-pointer transition-colors ${
                                      (descHighlightIdx[index] ?? -1) === descriptionOptions.filter(o => o.label.toLowerCase().includes((descDropdown[index]?.search || '').toLowerCase())).indexOf(opt)
                                        ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-800 dark:text-blue-200'
                                        : item.description === opt.value
                                          ? 'bg-blue-50 dark:bg-blue-500/15 font-bold text-blue-700 dark:text-blue-300'
                                          : 'text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800'
                                    }`}
                                  >
                                    {opt.label}
                                  </li>
                                ))}
                              {descriptionOptions.filter(opt => opt.label.toLowerCase().includes((descDropdown[index]?.search || '').toLowerCase())).length === 0 && (
                                <li className="px-3.5 py-6 text-[13px] text-slate-400 text-center">No results found</li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                      {lineItemErrors[index]?.description && <p className="text-xs text-red-500 mt-1">{lineItemErrors[index].description}</p>}
                    </div>

                    {/* HSN */}
                    <div className="md:col-span-2">
                      <label className="block text-[11.5px] font-bold text-slate-600 dark:text-slate-400 mb-1.5">
                        HSN Code
                      </label>
                      <input
                        type="text"
                        value={item.hsn}
                        readOnly
                        className="w-full h-11 px-3.5 text-[13px] font-semibold rounded-xl flex items-center bg-slate-100 border border-slate-200 text-slate-600 cursor-not-allowed dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300"
                      />
                    </div>

                    {/* Rate */}
                    <div className="md:col-span-3">
                      <label className="block text-[11.5px] font-bold text-slate-600 dark:text-slate-400 mb-1.5">
                        Rate (₹) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={item.rate}
                        onChange={(e) => { const v = e.target.value; if (/^\d*\.?\d*$/.test(v)) { handleLineItemChange(index, 'rate', v); setLineItemErrors(prev => { const n={...prev}; if(n[index]) { delete n[index].rate; if(!Object.keys(n[index]).length) delete n[index]; } return n; }); } }}
                        placeholder="0.00"
                        className="w-full h-11 px-3.5 text-[13px] font-semibold rounded-xl outline-none transition bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 dark:bg-slate-800/70 dark:border-slate-700 dark:text-slate-100"
                      />
                      {lineItemErrors[index]?.rate && <p className="text-xs text-red-500 mt-1">{lineItemErrors[index].rate}</p>}
                    </div>

                    {/* Quantity */}
                    <div className="md:col-span-2">
                      <label className="block text-[11.5px] font-bold text-slate-600 dark:text-slate-400 mb-1.5">
                        Qty <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={item.qty}
                        onKeyDown={(e) => { if (['.', ',', 'e', 'E', '+', '-'].includes(e.key)) e.preventDefault(); }}
                        onChange={(e) => { const v = e.target.value; if (/^\d*$/.test(v)) { handleLineItemChange(index, 'qty', v); setLineItemErrors(prev => { const n={...prev}; if(n[index]) { delete n[index].qty; if(!Object.keys(n[index]).length) delete n[index]; } return n; }); } }}
                        onBlur={() => { const whole = toWholeQty(item.qty); if (whole !== String(item.qty)) handleLineItemChange(index, 'qty', whole); }}
                        placeholder="0"
                        className="w-full h-11 px-3.5 text-[13px] font-semibold rounded-xl outline-none transition bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 dark:bg-slate-800/70 dark:border-slate-700 dark:text-slate-100"
                      />
                      {lineItemErrors[index]?.qty && <p className="text-xs text-red-500 mt-1">{lineItemErrors[index].qty}</p>}
                    </div>

                  </div>

                  {/* Calculated Amount */}
                  {item.rate && item.qty && (() => {
                    const rawTaxable = parseFloat(String(item.rate).replace(/,/g,'')) * parseFloat(String(item.qty).replace(/,/g,''));
                    const gstPct = parseFloat(item.gst) || 0;
                    const rawHalfGst = (rawTaxable * gstPct) / 200;
                    const rawFullGst = (rawTaxable * gstPct) / 100;
                    const fmt = (v) => v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    return (
                    <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-800 space-y-1">
                      <div className="flex justify-between items-center text-[12.5px]">
                        <span className="text-slate-500 dark:text-slate-400">Taxable value:</span>
                        <span className="font-bold text-slate-800 dark:text-slate-100">₹{fmt(rawTaxable)}</span>
                      </div>
                      {gstPct > 0 && (
                        formData.customerGSTIN.startsWith('07') ? (
                          <>
                            <div className="flex justify-between items-center text-[12.5px]">
                              <span className="font-semibold text-emerald-600 dark:text-emerald-400">CGST ({gstPct / 2}%):</span>
                              <span className="font-bold text-slate-800 dark:text-slate-100">₹{fmt(rawHalfGst)}</span>
                            </div>
                            <div className="flex justify-between items-center text-[12.5px]">
                              <span className="font-semibold text-violet-600 dark:text-violet-400">SGST ({gstPct / 2}%):</span>
                              <span className="font-bold text-slate-800 dark:text-slate-100">₹{fmt(rawHalfGst)}</span>
                            </div>
                          </>
                        ) : (
                          <div className="flex justify-between items-center text-[12.5px]">
                            <span className="font-semibold text-blue-600 dark:text-blue-400">IGST ({gstPct}%):</span>
                            <span className="font-bold text-slate-800 dark:text-slate-100">₹{fmt(rawFullGst)}</span>
                          </div>
                        )
                      )}
                    </div>
                    );
                  })()}
                </div>
              ))}
            </div>

            {/* Add Item button — always visible, validates on click */}

            {/* ── Auto items from shipment activities (with delete option) ── */}
            {visibleAutoItems.length > 0 && (
              <div className="mt-6 pt-5 border-t border-slate-200 dark:border-slate-800">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 text-[10.5px] font-black uppercase tracking-[0.12em]
                                   text-emerald-600 dark:text-emerald-400">
                    <Settings size={12} /> Default charges from this shipment
                  </span>
                  <span className="px-2 py-0.5 rounded-full text-[10.5px] font-bold
                                   bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                    {visibleAutoItems.length}
                  </span>
                  <span className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
                  <span className="text-[11px] text-slate-400 dark:text-slate-500">
                    Read-only — remove one if it should not be billed
                  </span>
                </div>
                <div className="space-y-4">
                {autoItems.map((item, i) => {
                  if (removedAutoItems.includes(i)) return null;
                  const visibleIdx = autoItems.slice(0, i + 1).filter((_, j) => !removedAutoItems.includes(j)).length - 1;
                  return (
                  <div
                    key={i}
                    className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 opacity-90 dark:border-slate-800 dark:bg-slate-900/40"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-slate-300 dark:text-slate-700"><GripVertical size={18} /></span>
                        <span className="inline-flex items-center gap-2">
                          <span className="grid place-items-center w-6 h-6 rounded-lg text-[11px] font-black
                                           bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-200">
                            {formData.lineItems.length + visibleIdx + 1}
                          </span>
                          <span className="text-[13.5px] font-black uppercase tracking-wide truncate
                                           text-slate-900 dark:text-white">{item.description || `Item ${formData.lineItems.length + visibleIdx + 1}`}</span>
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-1 rounded-full text-[11px] font-bold
                                         bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">Auto · 18% GST</span>
                        <button
                          type="button"
                          onClick={() => setRemovedAutoItems(prev => [...prev, i])}
                          className="p-1.5 rounded-lg text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                          title="Remove this charge"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                      {/* Description */}
                      <div className="md:col-span-5">
                        <label className="block text-[11.5px] font-bold text-slate-600 dark:text-slate-400 mb-1.5">Description</label>
                        <div className="w-full h-10 px-3 text-[13px] font-semibold rounded-xl border border-slate-200 bg-slate-100 text-slate-600 cursor-not-allowed dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 flex items-center">
                          {item._label}
                        </div>
                      </div>
                      {/* HSN */}
                      <div className="md:col-span-2">
                        <label className="block text-[11.5px] font-bold text-slate-600 dark:text-slate-400 mb-1.5">HSN Code</label>
                        <input
                          type="text"
                          value={item.hsn || ''}
                          readOnly
                          className="w-full h-10 px-3 text-[13px] font-semibold rounded-xl border border-slate-200 bg-slate-100 text-slate-600 cursor-not-allowed dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 flex items-center"
                        />
                      </div>
                      {/* Rate */}
                      <div className="md:col-span-3">
                        <label className="block text-[11.5px] font-bold text-slate-600 dark:text-slate-400 mb-1.5">Rate</label>
                        <input
                          type="text"
                          value={parseFloat(item.rate).toFixed(2)}
                          readOnly
                          className="w-full h-10 px-3 text-[13px] font-semibold rounded-xl border border-slate-200 bg-slate-100 text-slate-600 cursor-not-allowed dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 flex items-center"
                        />
                      </div>
                      {/* Qty */}
                      <div className="md:col-span-2">
                        <label className="block text-[11.5px] font-bold text-slate-600 dark:text-slate-400 mb-1.5">Qty</label>
                        <input
                          type="text"
                          value="1"
                          readOnly
                          className="w-full h-10 px-3 text-[13px] font-semibold rounded-xl border border-slate-200 bg-slate-100 text-slate-600 cursor-not-allowed dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 flex items-center text-center"
                        />
                      </div>
                    </div>

                    {/* GST Breakdown for auto items */}
                    {(() => {
                      const rawRate = parseFloat(item.rate);
                      const rawHalf = rawRate * 9 / 100;
                      const rawFull = rawRate * 18 / 100;
                      const fmt = (v) => v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                      return (
                    <div className="mt-3 px-3.5 py-2.5 rounded-xl bg-white border border-slate-200
                                    dark:bg-slate-900/60 dark:border-slate-800">
                      <div className="flex justify-between items-center text-[12.5px]">
                        <span className="text-slate-500 dark:text-slate-400">Taxable value:</span>
                        <span className="font-bold text-slate-800 dark:text-slate-100">₹{fmt(rawRate)}</span>
                      </div>
                      {formData.customerGSTIN.startsWith('07') ? (
                        <>
                          <div className="flex justify-between items-center text-[12.5px]">
                            <span className="font-semibold text-emerald-600 dark:text-emerald-400">CGST (9%):</span>
                            <span className="font-bold text-slate-800 dark:text-slate-100">₹{fmt(rawHalf)}</span>
                          </div>
                          <div className="flex justify-between items-center text-[12.5px]">
                            <span className="font-semibold text-violet-600 dark:text-violet-400">SGST (9%):</span>
                            <span className="font-bold text-slate-800 dark:text-slate-100">₹{fmt(rawHalf)}</span>
                          </div>
                        </>
                      ) : (
                        <div className="flex justify-between items-center text-[12.5px]">
                          <span className="font-semibold text-blue-600 dark:text-blue-400">IGST (18%):</span>
                          <span className="font-bold text-slate-800 dark:text-slate-100">₹{fmt(rawFull)}</span>
                        </div>
                      )}
                    </div>
                      );
                    })()}
                  </div>
                  );
                })}
                </div>
              </div>
            )}
            </div>
          </section>

        </div>

        {/* ── what it adds up to, and the way out ── */}
        <aside className="lg:sticky lg:top-6 space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden
                          dark:border-slate-800 dark:bg-[#0e1626] dark:shadow-none">
            <div className="flex items-center gap-2.5 px-5 py-4 border-b border-slate-100 dark:border-slate-800
                            bg-gradient-to-r from-blue-50/80 to-transparent dark:from-blue-500/[0.07]">
              <span className="grid place-items-center w-9 h-9 rounded-xl flex-shrink-0
                               bg-blue-600 text-white"><FileText size={17} /></span>
              <div>
                <h3 className="text-[14px] font-bold text-slate-900 dark:text-white">Invoice summary</h3>
                <p className="text-[11.5px] text-slate-500 dark:text-slate-400">
                  {runningTotals.intra ? 'Taxed as CGST + SGST' : 'Taxed as IGST'}
                </p>
              </div>
            </div>

            <div className="px-5 py-4 space-y-2.5">
              <div className="flex justify-between text-[13px]">
                <span className="text-slate-500 dark:text-slate-400">Total items</span>
                <span className="font-bold text-slate-800 dark:text-slate-100">{runningTotals.counted}</span>
              </div>
              <div className="flex justify-between text-[13px]">
                <span className="text-slate-500 dark:text-slate-400">Subtotal (taxable)</span>
                <span className="font-bold text-slate-800 dark:text-slate-100">{money(runningTotals.taxable)}</span>
              </div>
              {runningTotals.intra ? (
                <>
                  <div className="flex justify-between text-[13px]">
                    <span className="font-bold text-emerald-600 dark:text-emerald-400">CGST (9%)</span>
                    <span className="font-bold text-slate-800 dark:text-slate-100">{money(runningTotals.tax / 2)}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="font-bold text-violet-600 dark:text-violet-400">SGST (9%)</span>
                    <span className="font-bold text-slate-800 dark:text-slate-100">{money(runningTotals.tax / 2)}</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between text-[13px]">
                  <span className="font-bold text-blue-600 dark:text-blue-400">IGST (18%)</span>
                  <span className="font-bold text-slate-800 dark:text-slate-100">{money(runningTotals.tax)}</span>
                </div>
              )}

              <div className="flex justify-between items-center mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                <span className="text-[14px] font-black text-blue-600 dark:text-blue-400">Grand total</span>
                <span className="text-[22px] font-black text-blue-600 dark:text-blue-400 leading-none">
                  {money(runningTotals.grand)}
                </span>
              </div>
            </div>
          </div>

          {/* Bank Details Preview */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden
                          dark:border-slate-800 dark:bg-[#0e1626] dark:shadow-none">
            <div className="flex items-center gap-2.5 px-5 py-4 border-b border-slate-100 dark:border-slate-800
                            bg-gradient-to-r from-violet-50/80 to-transparent dark:from-violet-500/[0.07]">
              <span className="grid place-items-center w-9 h-9 rounded-xl flex-shrink-0
                               bg-violet-600 text-white"><Landmark size={17} /></span>
              <h4 className="text-[14px] font-bold text-slate-900 dark:text-white">Selected bank details</h4>
            </div>
            <div className="p-5">
            <div className="grid grid-cols-1 gap-2 text-[12.5px]">
              <div>
                <span className="text-slate-500 dark:text-slate-400">Bank:</span>
                <span className="ml-2 font-bold text-slate-800 dark:text-slate-100">
                  {bankDetails[formData.bankName].bankName}
                </span>
              </div>
              <div>
                <span className="text-slate-500 dark:text-slate-400">A/c No:</span>
                <span className="ml-2 font-bold text-slate-800 dark:text-slate-100">
                  {bankDetails[formData.bankName].accountNumber}
                </span>
              </div>
              <div>
                <span className="text-slate-500 dark:text-slate-400">IFSC:</span>
                <span className="ml-2 font-bold text-slate-800 dark:text-slate-100">
                  {bankDetails[formData.bankName].ifscCode}
                </span>
              </div>
              <div>
                <span className="text-slate-500 dark:text-slate-400">Branch:</span>
                <span className="ml-2 font-bold text-slate-800 dark:text-slate-100">
                  {bankDetails[formData.bankName].bankBranch}
                </span>
              </div>
            </div>
            </div>
          </div>

          {/* ── the way out ── */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden
                          dark:border-slate-800 dark:bg-[#0e1626] dark:shadow-none">
            <div className="flex items-center gap-2.5 px-5 py-4 border-b border-slate-100 dark:border-slate-800
                            bg-gradient-to-r from-emerald-50/80 to-transparent dark:from-emerald-500/[0.07]">
              <span className="grid place-items-center w-9 h-9 rounded-xl flex-shrink-0
                               bg-emerald-600 text-white"><ClipboardCheck size={17} /></span>
              <h4 className="text-[14px] font-bold text-slate-900 dark:text-white">Actions</h4>
            </div>

            <div className="p-5 flex flex-col gap-2.5">
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2.5 h-11 rounded-xl text-[14px] font-bold text-white
                           bg-blue-600 hover:bg-blue-700 transition shadow-sm"
              >
                <Eye size={18} /> Preview invoice
              </button>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center justify-center h-11 rounded-xl text-[14px] font-bold border transition
                           border-slate-200 bg-white text-slate-700 hover:bg-slate-50
                           dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200 dark:hover:bg-slate-700/60"
              >
                Cancel
              </button>
              <p className="text-[11px] text-slate-400 text-center mt-1">
                The invoice is saved and downloaded from the preview — nothing leaves this screen yet.
              </p>
            </div>
          </div>
        </aside>
        </form>
      </div>
    </div>

      {/* Add New Customer Modal */}
      {showAddCustomer && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl shadow-2xl p-6
                          bg-white border border-slate-200 dark:bg-[#0e1626] dark:border-slate-800">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-[16px] font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <UserPlus size={20} className="text-blue-600" />
                Add New Customer
              </h3>
              <button
                type="button"
                onClick={() => { setShowAddCustomer(false); setNewCustomer({ name: '', gstNo: '', address: '' }); }}
                className="h-9 w-9 grid place-items-center rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200 transition"
              >
                <X size={18} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Customer Name */}
              <div>
                <label className="block text-[12px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                  Customer Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newCustomer.name}
                  onChange={e => setNewCustomer(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Global Industries Pvt. Ltd."
                  className="h-11 w-full rounded-xl px-3.5 text-sm bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700/70 text-slate-800 dark:text-slate-100 placeholder-slate-400 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition"
                  autoFocus
                />
              </div>

              {/* GST No */}
              <div>
                <label className="block text-[12px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                  GST No
                </label>
                <input
                  type="text"
                  value={newCustomer.gstNo}
                  onChange={e => setNewCustomer(prev => ({ ...prev, gstNo: e.target.value.toUpperCase() }))}
                  placeholder="e.g., 07AABCT1332L1ZT"
                  maxLength={15}
                  className={`h-11 w-full px-3.5 text-sm rounded-xl border bg-slate-50 dark:bg-slate-800/70 text-slate-800 dark:text-slate-100 outline-none focus:ring-4 transition uppercase font-mono ${
                    newCustomer.gstNo.trim() && customerList.find(c => c.gstNo && c.gstNo.trim().toUpperCase() === newCustomer.gstNo.trim().toUpperCase())
                      ? 'border-red-400 focus:border-red-500 focus:ring-red-200'
                      : 'border-slate-200 dark:border-slate-700 focus:border-blue-400 focus:ring-blue-500/10'
                  }`}
                />
                {newCustomer.gstNo.trim() && (() => {
                  const dup = customerList.find(c => c.gstNo && c.gstNo.trim().toUpperCase() === newCustomer.gstNo.trim().toUpperCase());
                  return dup ? (
                    <p className="text-xs text-red-500 mt-1 font-medium">⚠️ Already exists for "{dup.name}"</p>
                  ) : null;
                })()}
              </div>

              {/* Address */}
              <div>
                <label className="block text-[12px] font-bold text-slate-700 dark:text-slate-300 mb-1.5">
                  Address
                </label>
                <textarea
                  value={newCustomer.address}
                  onChange={e => setNewCustomer(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="e.g., 10/54 Ground Floor, Karol Bagh, New Delhi - 110005"
                  rows={3}
                  className="w-full rounded-xl px-3.5 text-sm bg-slate-50 dark:bg-slate-800/70 border border-slate-200 dark:border-slate-700/70 text-slate-800 dark:text-slate-100 placeholder-slate-400 outline-none focus:border-blue-400 focus:ring-4 focus:ring-blue-500/10 transition py-2.5 resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => { setShowAddCustomer(false); setNewCustomer({ name: '', gstNo: '', address: '' }); }}
                className="flex-1 h-11 rounded-xl text-[13px] font-bold transition
                           border border-slate-200 bg-white text-slate-600 hover:bg-slate-50
                           dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddCustomer}
                disabled={addingCustomer || !newCustomer.name.trim() || !!(newCustomer.gstNo.trim() && customerList.find(c => c.gstNo && c.gstNo.trim().toUpperCase() === newCustomer.gstNo.trim().toUpperCase()))}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-xl font-semibold transition flex items-center justify-center gap-2"
              >
                {addingCustomer ? (
                  <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Saving...</>
                ) : (
                  <><UserPlus size={16} /> Add Customer</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InvoiceGenerationPage;
